'use client';

import { FC, useEffect, useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useInvoiceContext } from '@/contexts/invoice-context';
import { useTranslations } from '@/hooks/useTranslations';
import { EditInvoiceForm } from './edit-invoice-form';
import { IInvoice } from '@/types/invoice';
import { getInvoices } from '@/actions/invoice/invoice-queries';

export const EditInvoicePopup: FC = () => {
  const { dispatch, state } = useInvoiceContext();
  const t = useTranslations('EditInvoicePopup');

  const [invoices, setInvoices] = useState<IInvoice[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        const data = await getInvoices();

        const formattedInvoices = data.invoices.map((invoice) => ({
          ...invoice,
          items: invoice.items.map((item) => ({
            ...item,
            price: +item.price,
            qty: +item.qty,
            tax: +item.tax,
          })),
        }));

        setInvoices(Array.isArray(formattedInvoices) ? formattedInvoices : []);
      } catch (error) {
        console.error('Error fetching invoices:', error);
        setInvoices([]);
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
  }, []);

  const invoice = invoices.find((item) => item.id === state.invoiceId);

  if (loading) return <>Loading...</>;
  if (!invoice) return <>Error: Invoice not found</>;

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen && state.invoiceId) {
      dispatch({
        type: 'OPEN_EDIT_INVOICE_POPUP',
        payload: { invoiceId: state.invoiceId },
      });
    } else {
      dispatch({
        type: 'CLOSE_EDIT_INVOICE_POPUP',
      });
    }
  };

  return (
    <Sheet open={state.isEditInvoicePopupOpen} onOpenChange={handleOpenChange}>
      <SheetContent className="bg-white dark:bg-black">
        <SheetHeader>
          <SheetTitle>{t('title')}</SheetTitle>
          <SheetDescription>{t('description')}</SheetDescription>
        </SheetHeader>

        <div className="grid gap-4 p-4">
          <EditInvoiceForm invoice={invoice} />
        </div>
      </SheetContent>
    </Sheet>
  );
};
